import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import {
  ACTIVATION_REQUEST_STATUS_LABELS,
  ACTIVATION_TURNAROUND,
  PLATFORM_ASSISTED_FEATURES,
  type PlatformAssistedFeatureKey,
  errorWords,
} from "@alm/domain";
import { RequestActivationDialog } from "@/components/app/activation/request-activation-dialog";
import { PageHeader } from "@/components/app/shell/page-header";
import { useBuildingInView } from "@/components/app/rails/settings-scope-line";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";

export const Route = createFileRoute("/_authenticated/_administration/settings/activation")({
  beforeLoad: ({ context }) => guardRoute(context.me, "activation"),
  component: ActivationSettings,
});

/** What the People AI team switches on for a customer, and where each ask stands. */
function ActivationSettings() {
  const { facilityId } = useFacility();
  const oneBuilding = facilityId === "all" ? null : facilityId;
  const building = useBuildingInView();
  /* Asking names its building and is checked there; sending one on is checked company-wide,
     because the API cannot tell a request's building from its id. */
  const canAsk = useCan("settings:manage", oneBuilding);
  const canSubmit = useCan("settings:manage");
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState<PlatformAssistedFeatureKey | null>(null);
  const requests = useQuery(orpc.administration.activation.list.queryOptions());
  const submit = useMutation(
    orpc.administration.activation.submit.mutationOptions({
      onSuccess: async () => {
        toast.success("Sent to the People AI team", {
          description: `It usually takes ${ACTIVATION_TURNAROUND}.`,
        });
        await queryClient.invalidateQueries({
          queryKey: orpc.administration.activation.list.key(),
        });
      },
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );
  const rows = requests.data ?? [];
  /* A request for the whole organization counts at every building; a building's, only there. */
  const reaches = (row: { facilityId: string | null }) =>
    row.facilityId === null || row.facilityId === oneBuilding;
  const openFor = (key: string) =>
    rows.find((row) => row.featureKey === key && row.status !== "active" && reaches(row));
  const activeFor = (key: string) =>
    rows.some((row) => row.featureKey === key && row.status === "active" && reaches(row));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Activation requests"
        meta="Some features need a number, a line, an account or a credential that the People AI team provisions. Ask here; anyone can, and an administrator sends it on."
      />
      <Card>
        <CardHeader>
          <CardTitle>What the team switches on</CardTitle>
          <CardDescription>
            {oneBuilding
              ? `A new request is for ${building.name ?? "the building in view"}. To ask for another building, choose it at the top.`
              : "A new request is for the whole organization."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="divide-y" data-testid="list-platform-assisted-features">
            {PLATFORM_ASSISTED_FEATURES.map((feature) => {
              const open = openFor(feature.key);
              const active = activeFor(feature.key);
              return (
                <li
                  key={feature.key}
                  className="flex flex-wrap items-center gap-3 py-3 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{feature.label}</p>
                    <p className="text-muted-foreground text-sm">The team {feature.does}.</p>
                  </div>
                  {/* Not a button until the requests are read: offered early, it
                      asks again for something already on its way. */}
                  {requests.isPending ? (
                    <Skeleton className="h-7 w-36" />
                  ) : active ? (
                    <Badge variant="ok">Active</Badge>
                  ) : open ? (
                    <Badge variant={open.status === "awaiting_admin" ? "warn" : "ok"}>
                      {ACTIVATION_REQUEST_STATUS_LABELS[open.status]}
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={`${canAsk ? "Request activation" : "Ask my administrator"}: ${feature.label}`}
                      onClick={() => setAsking(feature.key)}
                    >
                      {canAsk ? "Request activation" : "Ask my administrator"}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {requests.isPending ? (
            <RowsSkeleton />
          ) : requests.isError ? (
            <ReadFailed
              message="Could not load the requests."
              error={requests.error}
              onRetry={() => void requests.refetch()}
              retrying={requests.isRefetching}
            />
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No requests yet. Ask for a feature above and it is tracked here.
            </p>
          ) : (
            <ul className="divide-y" data-testid="list-activation-requests">
              {rows.map((row) => {
                const feature = PLATFORM_ASSISTED_FEATURES.find((f) => f.key === row.featureKey);
                return (
                  <li key={row.id} className="flex flex-wrap items-start gap-3 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{feature?.label ?? row.featureKey}</span>
                        <Badge
                          variant={
                            row.status === "active"
                              ? "ok"
                              : row.status === "awaiting_admin"
                                ? "warn"
                                : "neutral"
                          }
                        >
                          {ACTIVATION_REQUEST_STATUS_LABELS[row.status]}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground text-sm">
                        {row.facilityName ?? "Whole organization"}
                        {row.requestedByName ? `, ${row.requestedByName}` : ""},{" "}
                        {new Date(row.createdAt).toLocaleDateString()}
                      </p>
                      <p className="text-sm whitespace-pre-wrap">{row.details}</p>
                    </div>
                    {row.status === "awaiting_admin" && canSubmit ? (
                      <Button
                        size="sm"
                        disabled={submit.isPending}
                        onClick={() => submit.mutate({ requestId: row.id })}
                        data-testid="button-submit-activation"
                      >
                        Send to the People AI team
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {asking ? (
        <RequestActivationDialog
          key={`${asking}:${oneBuilding ?? "organization"}`}
          featureKey={asking}
          facilityId={oneBuilding}
          open
          onOpenChange={(open) => (open ? null : setAsking(null))}
        />
      ) : null}
    </div>
  );
}
